import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { CustomLayoutRenderer } from '@/components/autoforms/CustomLayoutRenderer';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { AssigneePicker } from '@/components/autoforms/AssigneePicker';
import { emitDataChanged } from '@/lib/dataEvents';
import { CopyButton } from '@/components/ui/CopyButton';

// ============================================================================
// CustomFormContent — содержимое ветки КАСТОМНОЙ формы объекта (вынесено из
// AutoObjectForm, E2e7): окно создания записи дин.списка (dlCreate), окно
// «Назначить исполнителя» (assign_open), баннер валидации и рендер макета
// (CustomLayoutRenderer с атрибутами объекта+реквизитами формы).
// ЧИСТО отображательный компонент (SRP): ВСЯ запись/состояние в AutoObjectForm.
// ============================================================================

export interface CustomFieldAttr {
  name: string;
  type: string;
  label: string;
  selectionParams?: Array<{ field: string; condition: string; value?: string }>;
  selectionLinks?: Array<{ param: string; field: string }>;
  fkTable?: string;
  refTable?: string;
  types?: string[];
  // Значения перечисления (для селекта на макете)
  options?: Array<string | { value: string; label: string }>;
}

export interface CustomTsAttr {
  name: string;
  synonym: string;
  attributes: any[];
}

interface CustomFormContentProps {
  dlCreate: { type: string; name: string; initial: Record<string, unknown> } | null;
  dlOpen: { type: string; name: string; id: string } | null;
  assignOpen: boolean;
  assignBusy: boolean;
  assignTitle: string;
  validationError: string | null;
  formKey: string;
  elements: any[];
  displayNames: Record<string, string>;
  onDisplayNamesChange: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  tsData: Record<string, Array<Record<string, unknown>>>;
  onTsDataChange: (updater: (prev: Record<string, Array<Record<string, unknown>>>) => Record<string, Array<Record<string, unknown>>>) => void;
  objectAttrs: CustomFieldAttr[];
  tabularSections: CustomTsAttr[];
  formReadOnly: boolean;
  formEnabled: boolean;
  hiddenFields: Record<string, boolean>;
  formAttrs: Array<{ name: string; source?: { type?: string; table?: string; filters?: Array<{ field: string; condition: string; value?: string }>; commands?: string[] } }>;
  recordId?: string;
  onDlCreateClose: () => void;
  onDlOpenClose: () => void;
  onAssignClose: () => void;
  onAssignSelect: (id: string) => void;
  onPick: (attr: { name: string; types?: Array<{ typeName: string; fullName: string; objectType: string }> | string[]; fkTable?: string; refTable?: string; selectionParams?: Array<{ field: string; condition: string; value?: string }>; selectionLinks?: Array<{ param: string; field: string }> }, tsName?: string, rowIndex?: number) => void;
  onPickTs: (tsName: string, rowIndex: number, colAttr: { name: string; types?: Array<{ typeName: string; fullName: string; objectType: string }> }) => void;
  onCreateElement: (table: string, filters: Array<{ field: string; condition: string; value?: string }>, objectId: string) => void;
  onOpenElement: (table: string, id: string) => void;
  onPickMulti: (tsName: string, colMeta: any) => void;
  onCommand: (command: string) => void;
}

export function CustomFormContent({
  dlCreate, dlOpen, assignOpen, assignBusy, assignTitle, validationError, formKey,
  elements, displayNames, onDisplayNamesChange, tsData, onTsDataChange,
  objectAttrs, tabularSections, formReadOnly, formEnabled, hiddenFields, formAttrs,
  recordId, onDlCreateClose, onDlOpenClose, onAssignClose, onAssignSelect, onPick, onPickTs,
  onCreateElement, onOpenElement, onPickMulti, onCommand,
}: CustomFormContentProps) {
  return (
    <>
      {dlCreate && (
        <AutoObjectForm
          objectType={dlCreate.type}
          objectName={dlCreate.name}
          initialData={dlCreate.initial}
          // Создание из «+ Создать» → локальное событие «данные изменились»:
          // списки (дин.список заявки, виджеты рабочего стола) обновляются СРАЗУ.
          onCreated={() => emitDataChanged({ table: dlCreate.name, action: 'created' })}
          onClose={() => { emitDataChanged({ table: dlCreate.name, action: 'created' }); onDlCreateClose(); }}
        />
      )}
      {dlOpen && (
        // Клик по строке дин.списка → форма записи ИСТОЧНИКА (существующей).
        // После закрытия — событие «данные изменились» (список/виджет обновятся).
        <AutoObjectForm
          objectType={dlOpen.type}
          objectName={dlOpen.name}
          objectId={dlOpen.id}
          onClose={() => { emitDataChanged({ table: dlOpen.name, action: 'updated' }); onDlOpenClose(); }}
        />
      )}
      {assignOpen && (
        <UniversalWindow open onClose={onAssignClose} title="Назначить исполнителя" width="w-[min(480px,94vw)]" resizable={false}>
          <div className="p-2 space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Создастся задача по заявке «{assignTitle}» с выбранным исполнителем.
            </p>
            <AssigneePicker
              value=""
              taskType="tasks_by_orders"
              onChange={(id) => { if (id) onAssignSelect(id); }}
            />
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={onAssignClose} disabled={assignBusy}>
                {assignBusy ? 'Создаём…' : 'Отмена'}
              </Button>
            </div>
          </div>
        </UniversalWindow>
      )}
      {validationError && (
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
          <span className="flex-1 min-w-0 break-words">{validationError}</span>
          <CopyButton text={validationError} compact />
        </div>
      )}
      <CustomLayoutRenderer
        formKey={formKey}
        elements={elements}
        displayNames={displayNames}
        setDisplayNames={onDisplayNamesChange}
        tsData={tsData}
        setTsData={onTsDataChange}
        attributes={objectAttrs}
        tabularSections={tabularSections}
        formReadOnly={formReadOnly}
        formEnabled={formEnabled}
        hiddenFields={hiddenFields}
        formAttrs={formAttrs}
        recordId={recordId}
        onPick={onPick}
        onPickTs={onPickTs}
        onCreateElement={onCreateElement}
        onOpenElement={onOpenElement}
        onPickMulti={onPickMulti}
        onCommand={onCommand}
      />
    </>
  );
}
